/**
 * Страница «Добавить вручную» заказа СОУТ (`/orders/:id/sout/workplaces/new`, гард company;
 * p3-sout-workplaces §4/§14, Волна 7). Полная реализация §14: отдельная страница БЕЗ выбора
 * существующих мест/сотрудников структуры (для них — «Из структуры компании», модал §2). Клиент
 * заполняет ВСЕ блоки карточки места (§5–§12) — `WorkplaceForm` в режиме `manual` — на ЛОКАЛЬНОМ
 * черновике (`emptyManualSoutLine`, БЕЗ autosave: строки на сервере ещё нет). Единственная кнопка
 * сохранения — «Готово — к списку рабочих мест» (рендерится самой `WorkplaceForm`, §13) —
 * вызывает `submit()` вместо обычного «вернуться на список»: атомарно создаёт место (+сотрудника,
 * если введён) и строку заказа одним запросом (`POST .../sout/lines/manual`, D7) и ТОЛЬКО при
 * успехе уходит на шаг «Рабочие места» (`?step=3&line=<id>`, тот же контракт возврата, что и у
 * страницы карточки РМ, `order-sout-workplace.tsx`).
 *
 * Дедуп (§14, D7): дубликат сотрудника (СНИЛС) или места (пара подразделение+должность) — 422 с
 * `details`, ничего не создаётся; сервер — единственный источник истины (клиент не дублирует
 * бизнес-правило, а просто маппит `details` в поля формы через `serverFieldErrors`). Данные
 * черновика при ошибке НЕ сбрасываются (это и так локальный React-стейт — запрос его не трогает).
 *
 * Двойной клик/конкурентные сохранения (§14 критерий «не более одного»): `busyRef` — синхронный
 * guard ДО следующего рендера (React state обновляется асинхронно, одного `busy` в замыкании
 * недостаточно при быстрых повторных кликах в одном тике); `doneDisabled` дополнительно
 * дизейблит саму кнопку на время запроса. Авторитетная защита — на сервере (advisory-lock +
 * уникальность, `store.CreateManualSoutLine`).
 *
 * issue #12: неудачное «Готово — к списку рабочих мест» прокручивает страницу к первому
 * незаполненному обязательному полю (и ставит на него фокус) — иначе на длинной форме клиент
 * оставался внизу и не видел, что мешает сохранению. Работает для обоих источников ошибок:
 * клиентской проверки блока «Сотрудник» и 422 с сервера (см. `scrollToFirstInvalid`).
 */
import { useEffect, useRef, useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ApiError,
  companyApi,
  soutApi,
  type SoutAnketa,
  type SoutLine,
  type SoutWorkplaceMatch,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { computeLineMissing, emptyManualSoutLine, hasSnilsOrderConflict } from '@/lib/sout'
import { WorkplaceForm } from '@/sout/workplace-form'
import { invalidatePositionsCache } from '@/company/position-select'
import { SOUT_SNILS_ORDER_CONFLICT, SOUT_WORKPLACE_MANUAL_PAGE_SUBTITLE } from '@/sout/sout-copy'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Button } from '@/design-system/components/primitives/button'
import { Alert } from '@/design-system/components/feedback/alert'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'

/**
 * `details[].field` от `handleAddManualSoutLine` — поля сотрудника приходят с префиксом
 * `person.` (`person.snils`/`person.fullName`/`person.gender`), но
 * `WorkplaceForm` подсвечивает их БЕЗ префикса (те же ключи, что и `computeLineMissing`,
 * `fullName`/`snils`/`gender`) — снимаем префикс здесь, в одном месте.
 */
function mapFieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || !err.details?.length) return {}
  const map: Record<string, string> = {}
  for (const d of err.details) {
    const key = d.field.startsWith('person.') ? d.field.slice('person.'.length) : d.field
    map[key] = d.message
  }
  return map
}

/**
 * Прокрутка к первому незаполненному обязательному полю формы (issue #12). «Первое» — первое в
 * DOM-порядке, то есть в порядке расположения полей формы. Признак ошибки — `aria-invalid` на
 * контроле: его ставит `Field` и по локальной подсветке «дозаполнить» (`computeLineMissing`), и
 * по ошибке с сервера (`serverFieldErrors`), поэтому одна выборка покрывает оба источника из
 * критериев приёмки. Фокус переводится с `preventScroll`, чтобы не спорить с плавной прокруткой.
 */
function scrollToFirstInvalid(root: HTMLElement | null) {
  const el = root?.querySelector<HTMLElement>('[aria-invalid="true"]')
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  el.focus({ preventScroll: true })
}

/** Заголовок диалога подтверждения (centrika-docs!101, дословно). */
const DUPLICATE_DIALOG_TITLE = 'Такое рабочее место уже есть в структуре компании'

/**
 * Вопрос диалога (centrika-docs!101, дословно): с сотрудником — «…и назначить на него сотрудника
 * {ФИО}, СНИЛС {СНИЛС}?»; без заполненного ФИО — «…без сотрудника?».
 */
function duplicateQuestion(line: SoutLine): string {
  const position = line.conditions.position.trim()
  const department = line.conditions.departmentName.trim()
  const head = `Вы точно хотите создать ещё одно рабочее место «${position}» в подразделении «${department}»`
  const fullName = line.person.fullName.trim()
  if (!fullName) return `${head} без сотрудника?`
  return `${head} и назначить на него сотрудника ${fullName}, СНИЛС ${line.person.snils}?`
}

const PERSON_FIELD_MESSAGES: Record<string, string> = {
  fullName: 'Укажите ФИО сотрудника (3 слова, без инициалов)',
  snils: 'Укажите корректный СНИЛС (11 цифр)',
  gender: 'Укажите пол',
}

/**
 * Клиентская валидация блока «Сотрудник» ПЕРЕД запросом (та же семантика, что и серверный
 * `handleAddManualSoutLine`). Пустое ФИО — рабочее место без сотрудника, обязательных полей в
 * блоке нет (centrika-docs, `page.create-sout-order.new-workplace`, MR !13); при заполненном
 * ФИО обязательны ФИО, СНИЛС и пол (дата рождения из формы убрана, centrika-docs!93).
 * Пустая карта — блок заполнен корректно.
 */
function validatePersonBlock(line: SoutLine): Record<string, string> {
  const missing = computeLineMissing(line)
  const errors: Record<string, string> = {}
  for (const field of Object.keys(PERSON_FIELD_MESSAGES)) {
    if (missing.includes(field)) errors[field] = PERSON_FIELD_MESSAGES[field]
  }
  return errors
}

/**
 * Список найденных рабочих мест для диалога (centrika-docs!101): подразделение и должность
 * каждого места, под ними — назначенные сотрудники с ФИО и СНИЛС; место без сотрудника
 * помечается «Сотрудник не назначен».
 */
function DuplicateMatchesList({ matches }: { matches: SoutWorkplaceMatch[] }) {
  return (
    <div className="flex flex-col gap-3">
      {matches.map((m) => (
        <div key={m.id}>
          <p className="text-txt">
            {m.position} · {m.departmentName}
          </p>
          {m.persons.length === 0 ? (
            <p className="text-13 text-txt-secondary">Сотрудник не назначен</p>
          ) : (
            <ul className="mt-0.5 list-disc pl-5 text-13 text-txt-secondary">
              {m.persons.map((p) => (
                <li key={`${m.id}-${p.snils}-${p.fullName}`}>
                  {p.fullName}, СНИЛС {p.snils || '—'}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  )
}

export function OrderSoutWorkplaceNewPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [anketa, setAnketa] = useState<SoutAnketa | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [line, setLine] = useState<SoutLine>(() => emptyManualSoutLine())
  // p3-critical-fixes (№4, уточнение РП): существующие подразделения компании — опции селекта
  // «Структурное подразделение» в форме. Страница клиентская (гард company), поэтому источник —
  // `companyApi.listDepartments` (полный список структуры, не поисковые подсказки). Ошибка
  // загрузки НЕ блокирует форму: `departmentNames` остаётся пустым → форма показывает свободный
  // ввод названия (тот же результат, что и у компании без подразделений).
  const [departmentNames, setDepartmentNames] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const busyRef = useRef(false)
  const formRef = useRef<HTMLDivElement>(null)
  // Счётчик запросов прокрутки (issue #12): растёт на каждой неудачной попытке сохранения —
  // и клиентской, и серверной. Именно счётчик, а не флаг: повторное нажатие с той же ошибкой
  // обязано прокрутить снова, а прокрутка должна случиться ПОСЛЕ рендера ошибок (у поля с
  // ошибкой сервера `aria-invalid` появляется только вместе с этим рендером).
  const [scrollRequest, setScrollRequest] = useState(0)
  // Совпадающие рабочие места структуры (centrika-docs!101): непустой список открывает диалог
  // подтверждения; подтверждение повторяет то же сохранение с `confirmDuplicate`.
  const [duplicateMatches, setDuplicateMatches] = useState<SoutWorkplaceMatch[] | null>(null)

  useEffect(() => {
    if (scrollRequest === 0) return
    scrollToFirstInvalid(formRef.current)
  }, [scrollRequest])

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setLoading(true)
    setLoadError(null)
    soutApi
      .getAnketa(id, controller.signal)
      .then((ank) => {
        if (!controller.signal.aborted) setAnketa(ank)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setLoadError(getErrorMessage(err, 'Не удалось загрузить заказ'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [id])

  // Список подразделений грузим отдельно и «мягко»: неудача не выставляет ошибку страницы —
  // форма просто откатывается на свободный ввод названия подразделения (см. `departmentNames`).
  useEffect(() => {
    const controller = new AbortController()
    companyApi
      .listDepartments(controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setDepartmentNames(res.items.map((d) => d.name))
      })
      .catch(() => {
        // Тихо игнорируем — форма остаётся рабочей со свободным вводом подразделения.
      })
    return () => controller.abort()
  }, [])

  function goBack() {
    if (id) navigate(`/orders/${id}/sout?step=3`)
  }

  /**
   * Сохранение формы. `confirmDuplicate` — повтор ПОСЛЕ подтверждения диалога
   * (centrika-docs!101): совпадение подразделения и должности со структурой больше не запрет, но
   * без подтверждения клиента место не создаётся (сервер отклоняет такой запрос сам).
   */
  async function submit(confirmDuplicate = false) {
    if (!id || busyRef.current) return
    // MR !13: при заполненном ФИО блок «Сотрудник» должен быть полным — показываем ошибки у
    // полей и НЕ отправляем заведомо отклоняемый запрос. Пустое ФИО пропускается: создаётся
    // рабочее место без сотрудника. Сервер валидирует это же авторитетно (422).
    const personErrors = validatePersonBlock(line)
    if (Object.keys(personErrors).length > 0) {
      setFieldErrors(personErrors)
      setSubmitError('Проверьте данные сотрудника — при заполненном ФИО нужны СНИЛС и пол.')
      setScrollRequest((n) => n + 1)
      return
    }
    // Уникальность СНИЛС в заказе (centrika-docs!101): та же проверка, что подсвечивает поле,
    // здесь ещё и не даёт отправить форму. Сервер проверяет это же авторитетно.
    if (anketa && hasSnilsOrderConflict(line, anketa.lines)) {
      setFieldErrors({ snils: SOUT_SNILS_ORDER_CONFLICT })
      setSubmitError(SOUT_SNILS_ORDER_CONFLICT)
      setScrollRequest((n) => n + 1)
      return
    }
    busyRef.current = true
    setBusy(true)
    setSubmitError(null)
    setFieldErrors({})
    try {
      if (!confirmDuplicate) {
        const matches = await findDuplicateMatches(id)
        if (matches.length > 0) {
          // Диалог решает, создавать ли ещё одно такое место; форма и введённые данные целы.
          setDuplicateMatches(matches)
          return
        }
      }
      const created = await soutApi.addManualLine(id, {
        departmentName: line.conditions.departmentName.trim(),
        position: line.conditions.position.trim(),
        conditions: line.conditions,
        person: line.person,
        confirmDuplicate,
      })
      // Могла быть заведена новая должность вместе с рабочим местом (centrika-docs!85, паритет
      // с формой добавления РМ структуры docs!62) — сбрасываем модульный кэш справочника, чтобы
      // другие открытые формы (в т.ч. строгие) увидели её.
      invalidatePositionsCache()
      setDuplicateMatches(null)
      navigate(`/orders/${id}/sout?step=3&line=${created.id}`)
    } catch (err) {
      setFieldErrors(mapFieldErrors(err))
      setSubmitError(getErrorMessage(err, 'Не удалось создать рабочее место'))
      setScrollRequest((n) => n + 1)
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  /**
   * Совпадающие рабочие места структуры для диалога. Неудача запроса НЕ мешает сохранению:
   * авторитетная проверка всё равно на сервере (он отклонит создание без подтверждения), а
   * лишний блокирующий отказ на предпросмотре был бы хуже — клиент увидит ошибку от создания.
   */
  async function findDuplicateMatches(orderId: string): Promise<SoutWorkplaceMatch[]> {
    try {
      const res = await soutApi.workplaceMatches(orderId, {
        departmentName: line.conditions.departmentName.trim(),
        position: line.conditions.position.trim(),
      })
      return res.items
    } catch {
      return []
    }
  }

  const readOnly = anketa ? !anketa.canEdit : false

  return (
    <div>
      <div className="mb-4">
        <Button
          variant="secondary"
          size="sm"
          onClick={goBack}
          disabled={busy}
          leftIcon={<ArrowLeft className="size-4 stroke-current" aria-hidden />}
        >
          К списку рабочих мест
        </Button>
      </div>

      {loadError ? (
        <Alert variant="danger">{loadError}</Alert>
      ) : loading || !anketa ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : readOnly ? (
        <Alert variant="info">Заказ уже отправлен — добавление рабочих мест недоступно.</Alert>
      ) : (
        <>
          <PageHead title="Новое рабочее место" subtitle={SOUT_WORKPLACE_MANUAL_PAGE_SUBTITLE} />

          {submitError ? (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          ) : null}

          <div ref={formRef}>
            <WorkplaceForm
            line={line}
            readOnly={false}
            employees={[]}
            manual
            departmentOptions={departmentNames}
            onChange={setLine}
            onBack={() => void submit()}
            serverFieldErrors={fieldErrors}
            doneDisabled={busy}
            // centrika-docs!94: совпадения СНИЛС проверяются по рабочим местам текущего заказа
            // (уже загружены вместе с анкетой). Новая строка ещё не в списке — само с собой
            // сравнение невозможно.
              orderLines={anketa.lines}
            />
          </div>

          {/* centrika-docs!101: подтверждение создания ЕЩЁ ОДНОГО места с теми же подразделением
              и должностью. Закрытие крестиком/Escape равнозначно «Вернуться к форме»: ничего не
              создаётся, введённые данные остаются в полях. */}
          <ConfirmDialog
            open={duplicateMatches !== null}
            title={DUPLICATE_DIALOG_TITLE}
            message={duplicateQuestion(line)}
            details={<DuplicateMatchesList matches={duplicateMatches ?? []} />}
            closeDisabled={busy}
            onClose={() => setDuplicateMatches(null)}
            buttons={[
              {
                label: 'Создать рабочее место',
                variant: 'primary',
                disabled: busy,
                onClick: () => void submit(true),
              },
              {
                label: 'Вернуться к форме',
                variant: 'secondary',
                disabled: busy,
                onClick: () => setDuplicateMatches(null),
              },
            ]}
          />
        </>
      )}
    </div>
  )
}
